import { FC, useLayoutEffect, useRef } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import Cookies from 'js-cookie'
import './Charts.scss';



type ColumnChartProps = {
    name: string,
    data: any[],
    titleX?: string,
    titleY?: string,
    handleSelect?: (a: any) => void
    percentY?: boolean,
    fillColor?: number,
    enableColumnHighlight?: boolean,
    highlightColor?: number,
    hideXAxisLabels?: boolean
    avoidSorting?: boolean
    chartHeight?: number
};

const ColumnChart: FC<ColumnChartProps> = ({ name, data, titleX, titleY, handleSelect, percentY, fillColor, enableColumnHighlight, highlightColor, hideXAxisLabels, avoidSorting, chartHeight = 450 }) => {
    let sortedData = !avoidSorting ? Object.entries(data)
        .sort((a: any, b: any) => {
            return b[1].value - a[1].value
        }).map(e => e[1]) : data;
    const series: any = useRef(null);
    const chartId = name + Date.now().toString().substr(-4)
    useLayoutEffect(() => {
        am5.ready(function () {
            // Create root element
            // https://www.amcharts.com/docs/v5/getting-started/#Root_element
            const root = am5.Root.new(chartId);

            // Set themes
            // https://www.amcharts.com/docs/v5/concepts/themes/
            root.setThemes([am5themes_Animated.new(root)]);

            // Create chart
            // https://www.amcharts.com/docs/v5/charts/xy-chart/
            const chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panX: false,
                    panY: false,
                    wheelX: "none",
                    wheelY: "none"
                }),
            );
            let xRenderer = am5xy.AxisRendererX.new(root, {
                cellStartLocation: 0.1,
                cellEndLocation: 0.9, 
            });
            xRenderer.labels.template.setAll({
                forceHidden: hideXAxisLabels,
                oversizedBehavior: "wrap",
                maxWidth: 130, fontSize: Number(Cookies.get('globalFontSize'))*0.85, //12px,
                fill: (Cookies.get('darkMode') === "true") ? am5.color(0xd9d9d9) : am5.color(0x003b5c),
            });

            const xAxis = chart.xAxes.push(
                am5xy.CategoryAxis.new(root, {
                    categoryField: "name",
                    renderer: xRenderer,
                }),
            );

            titleX && xAxis.children.push(
                am5.Label.new(root, {
                    text: titleX,
                    fill: (Cookies.get('darkMode') === "true") ? am5.color(0xd9d9d9) : am5.color(0x003b5c),
                    fontWeight: "600",
                    fontStyle:"italic",
                    x: am5.p50,
                    centerX: am5.p50
                })
            );

            xAxis.data.setAll(sortedData);


            const yRenderer = am5xy.AxisRendererY.new(root, { minGridDistance: 20 });

            const yAxis = chart.yAxes.push(
                am5xy.ValueAxis.new(root, {
                    numberFormat: percentY ? "#'%'" : "#",
                    renderer: yRenderer,
                }),
            );
            titleY && yAxis.children.unshift(
                am5.Label.new(root, {
                    rotation: -90,
                    fill: (Cookies.get('darkMode') === "true") ? am5.color(0xd9d9d9) : am5.color(0x003b5c),
                    fontWeight: "600",
                    fontStyle: "italic",
                    text: titleY,
                    y: am5.p50,
                    centerX: am5.p50
                })
            );

            yRenderer.labels.template.setAll({
                fill: (Cookies.get('darkMode') === "true") ? am5.color(0xd9d9d9) : am5.color(0x003b5c),
                fontSize: Number(Cookies.get('globalFontSize'))*0.85, //12px,
            });


            


            // Add series
            // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
            series.current = chart.series.push(
                am5xy.ColumnSeries.new(root, {
                    name: 'Series',
                    xAxis: xAxis,
                    yAxis: yAxis,
                    valueYField: 'value',
                    categoryXField: 'name',
                    stacked: true,
                    fill: fillColor ? am5.color(fillColor) : ((Cookies.get('darkMode') === "true") ? am5.color(0xd9d9d9) : am5.color(0x487292)),
                    tooltip: am5.Tooltip.new(root, {
                        labelText: percentY ? "{name}:\n[bold]{valueY}%[/]" : "{name}:\n[bold]{valueY}[/]",
                    }),
                }),
            );
            series.current.data.setAll(sortedData);

            if (enableColumnHighlight) {
                series.current.columns.template.set("interactive", true);
                series.current.columns.template.states.create("highlight", {
                    fill: highlightColor ? am5.color(highlightColor) : ((Cookies.get('darkMode') === "true") ? am5.color(0x4e83b1) : am5.color(0x8ccbe9)),
                    strokeWidth: 4,
                    stroke: highlightColor ? am5.color(highlightColor) : ((Cookies.get('darkMode') === "true") ? am5.color(0xd9d9d9) : am5.color(0x487292))
                });
            }
            
            series.current.columns.template.events.on("click", function (ev: any) {
                if (handleSelect) {
                    handleSelect(ev.target.dataItem.dataContext);
                }

                if (enableColumnHighlight) {
                    series.current.columns.each(function (column: any) {
                        if (column !== ev.target)
                            column.states.applyAnimate("default");
                        else
                            column.states.applyAnimate("highlight");
                    });
                }
            });

            // Rounded corners for columns
            series.current.columns.template.setAll({
                cornerRadiusTR: 5,
                cornerRadiusTL: 5,
            });

            // Add cursor
            chart.set("cursor", am5xy.XYCursor.new(root, {
                behavior: "zoomXY"
            }));



            // Make stuff animate on load
            // https://www.amcharts.com/docs/v5/concepts/animations/
            series.current.appear(1000);
            chart.appear(1000, 100);
        });
    }, [enableColumnHighlight]);// eslint-disable-line

    return <div id={chartId} className="chart-container" style={{ height: chartHeight }} />;
};

export default ColumnChart;
